// ============================================================================
// EmployeeCreateModal — INSERT a new employees row (Stage 2, 2026-06-15).
//
// Rendered only for hr / superuser (the caller gates on role; RLS is the real
// enforcement — hr_write + superuser_all allow the INSERT). Mirrors the edit
// modal's field set so the two surfaces stay consistent. app_role is omitted
// here on purpose: it drives the auth chain and is managed in Admin, not HR.
//
// ⛔ Which group the new person lands in is decided by `pay_type`, and by
// NOTHING ELSE. This header used to describe the old rule — a TCS work email
// filed them under "Full employees", any other email under "W2 Hourly" — and
// the modal told the user so in its own subtitle. Both screens are gone
// (TCSD-0118): they agreed on an employment_type='W2' filter and so between them
// dropped every employee who was not W2. `pay_type` is asked for here directly
// rather than inferred, because leaving it unset drops the new hire straight
// onto the "Pay type not set" worklist on the day they are created.
// ============================================================================
import { useState, type FormEvent } from 'react';
import { X as XIcon } from 'lucide-react';
import { createEmployee } from './hooks/use-hr-data';
import { overlayClickClose } from '../../shared/overlay-close';
import { errorMessage } from '../../shared/errors';

// 1099 joined the employment_type CHECK 2026-08-25; this select had no way to
// say it, which is part of why no 1099 employee has ever been recorded.
const EMPLOYMENT_TYPES = ['W2', '1099', 'Subcontractor'];
const EMPLOYMENT_STATUSES = ['Active', 'Former'];
const PAY_TYPES = ['Salaried', 'Hourly'];

interface Props {
  onClose: () => void;
  onCreated: (employeeId: string) => void;
}

export default function EmployeeCreateModal({ onClose, onCreated }: Props) {
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [title, setTitle] = useState('');
  const [empType, setEmpType] = useState('W2');
  const [payType, setPayType] = useState('');
  const [status, setStatus] = useState('Active');
  const [emailWork, setEmailWork] = useState('');
  const [emailPersonal, setEmailPersonal] = useState('');
  const [phone, setPhone] = useState('');
  const [location, setLocation] = useState('');
  const [shift, setShift] = useState('');
  const [hireDate, setHireDate] = useState('');
  const [ecName, setEcName] = useState('');
  const [ecPhone, setEcPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    if (!first.trim() && !last.trim()) {
      setError('Enter a first or last name.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const newId = await createEmployee({
        employee_name_first: first.trim() || null,
        employee_name_last: last.trim() || null,
        employee_title: title.trim() || null,
        employment_type: empType || null,
        pay_type: payType || null,
        employment_status: status || null,
        email_work: emailWork.trim() || null,
        email_personal: emailPersonal.trim() || null,
        phone: phone.trim() || null,
        location: location.trim() || null,
        current_shift: shift.trim() || null,
        hire_date: hireDate || null,
        emergency_contact_name: ecName.trim() || null,
        emergency_contact_phone: ecPhone.trim() || null,
        notes: notes.trim() || null,
      });
      onCreated(newId);
    } catch (err) {
      console.error('[hr] employee create failed', err);
      setError(errorMessage(err, 'Could not create employee.'));
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-tcs-scrim/60 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Add employee"
      {...overlayClickClose(onClose)}
    >
      <form
        onClick={(ev) => ev.stopPropagation()}
        onSubmit={handleSubmit}
        className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-xl bg-tcs-overlay p-5 shadow-xl sm:rounded-xl"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-tcs-text">Add employee</h2>
            <p className="truncate text-xs text-tcs-text-muted">
              App role is assigned later in Admin. Pay type decides which group they appear
              under in the directory — set it now and they won&rsquo;t land on the worklist.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-tcs-text-muted hover:bg-tcs-surface"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name">
              <input value={first} onChange={(ev) => setFirst(ev.target.value)} className={inputCls} />
            </Field>
            <Field label="Last name">
              <input value={last} onChange={(ev) => setLast(ev.target.value)} className={inputCls} />
            </Field>
          </div>

          <Field label="Title">
            <input value={title} onChange={(ev) => setTitle(ev.target.value)} className={inputCls} />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Employment type">
              <select value={empType} onChange={(ev) => setEmpType(ev.target.value)} className={inputCls}>
                {EMPLOYMENT_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </Field>
            <Field label="Pay type">
              <select value={payType} onChange={(ev) => setPayType(ev.target.value)} className={inputCls}>
                <option value="">Not set — decide later</option>
                {PAY_TYPES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Status">
            <select value={status} onChange={(ev) => setStatus(ev.target.value)} className={inputCls}>
              {EMPLOYMENT_STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Work email">
              <input value={emailWork} onChange={(ev) => setEmailWork(ev.target.value)} className={inputCls} />
            </Field>
            <Field label="Personal email">
              <input value={emailPersonal} onChange={(ev) => setEmailPersonal(ev.target.value)} className={inputCls} />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone">
              <input value={phone} onChange={(ev) => setPhone(ev.target.value)} className={inputCls} />
            </Field>
            <Field label="Hire date">
              <input type="date" value={hireDate} onChange={(ev) => setHireDate(ev.target.value)} className={inputCls} />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Current shift">
              <input value={shift} onChange={(ev) => setShift(ev.target.value)} className={inputCls} />
            </Field>
            <Field label="Location">
              <input value={location} onChange={(ev) => setLocation(ev.target.value)} className={inputCls} />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Emergency contact name">
              <input value={ecName} onChange={(ev) => setEcName(ev.target.value)} className={inputCls} />
            </Field>
            <Field label="Emergency contact phone">
              <input value={ecPhone} onChange={(ev) => setEcPhone(ev.target.value)} className={inputCls} />
            </Field>
          </div>

          <Field label="Notes">
            <textarea rows={3} value={notes} onChange={(ev) => setNotes(ev.target.value)} className={inputCls} />
          </Field>
        </div>

        {error && (
          <p className="mt-3 rounded-md border border-rag-red bg-st-danger-bg p-2 text-xs text-st-danger-fg-strong">{error}</p>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-tcs-border px-3 py-2 text-sm text-tcs-text-secondary hover:bg-tcs-bg-muted"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-md bg-tcs-inverse px-4 py-2 text-sm font-medium text-tcs-inverse-ink hover:bg-tcs-inverse-hover disabled:opacity-50"
          >
            {submitting ? 'Creating…' : 'Create employee'}
          </button>
        </div>
      </form>
    </div>
  );
}

const inputCls =
  'w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-base focus:border-tcs-border-input focus:outline-none';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-xs">
      <span className="mb-1 block font-medium uppercase tracking-wide text-tcs-text-muted">{label}</span>
      {children}
    </label>
  );
}
